import { UncontrolledTextFieldDebounced } from '@if/mui'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import SearchIcon from '@mui/icons-material/Search'
import { IconButton, InputAdornment, Toolbar } from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import LoanListFiltersDialog from './LoanListFiltersDialog'

// Keep in sync with LoanDashboardFiltersValidator.SearchTermMaxLength on the backend.
const SEARCH_TERM_MAX_LENGTH = 100

interface LoanListOptionsProps {
  searchTerm: string
  onSearch: (searchTerm: string) => void
}

const LoanListOptions = ({ searchTerm, onSearch }: LoanListOptionsProps) => {
  const { t } = useTranslation()
  const [openFilterDialog, setopenFilterDialog] = useState<boolean>(false)
  // Bumped to remount the field: it is uncontrolled (defaultValue), so clearing the
  // form value is not enough to empty the visible input — a remount re-seeds it.
  const [searchFieldKey, setSearchFieldKey] = useState<number>(0)

  const { control, subscribe, setValue } = useForm<{ search: string }>({
    mode: 'onChange',
    defaultValues: { search: searchTerm },
  })
  const currentSearch = useWatch({ control, name: 'search' })

  useEffect(() => {
    const cancelSubscription = subscribe({
      formState: { values: true },
      callback: ({ values }) => onSearch((values.search ?? '').trim()),
    })
    return () => cancelSubscription()
  }, [subscribe, onSearch])

  const handleClear = () => {
    setValue('search', '')
    setSearchFieldKey((key) => key + 1)
  }

  return (
    <>
      <Toolbar disableGutters>
        <UncontrolledTextFieldDebounced
          key={searchFieldKey}
          control={control}
          name="search"
          label={t('common.search')}
          debounceTime={1000}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: currentSearch ? (
                <InputAdornment position="end">
                  <IconButton aria-label={t('common.clear')} size="small" edge="end" onClick={handleClear}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
            htmlInput: { maxLength: SEARCH_TERM_MAX_LENGTH },
          }}
        />
        <IconButton href="/loans/create" size="large">
          <AddIcon fontSize="inherit" />
        </IconButton>
        <IconButton size="large" onClick={() => setopenFilterDialog(true)}>
          <FilterAltIcon />
        </IconButton>
      </Toolbar>
      <LoanListFiltersDialog open={openFilterDialog} onClose={() => setopenFilterDialog(false)} onSubmit={() => {}} />
    </>
  )
}

export default LoanListOptions
